import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { ArrowLeft } from "lucide-react";
import { fetchMarkets, fetchHistory } from "@/lib/api";
import { fmtInt, fmtSigned, signClass } from "@/lib/format";

const WEEKS_OPTS = [12, 26, 52, 104];

function TipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-zinc-700 bg-black px-3 py-2 font-mono">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-[11px] tabular-nums">
          <span className="inline-block h-2 w-2" style={{ background: p.color }} />
          <span className="text-zinc-400 uppercase tracking-wider">{p.name}</span>
          <span className="ml-auto text-zinc-100">{fmtSigned(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Compare() {
  const [sp, setSp] = useSearchParams();
  const [a, setA] = useState(sp.get("a") || "099741");
  const [b, setB] = useState(sp.get("b") || "043602");
  const [weeks, setWeeks] = useState(parseInt(sp.get("weeks") || "52", 10));
  const [metric, setMetric] = useState(sp.get("metric") || "net_noncomm");

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("a", a);
    next.set("b", b);
    next.set("weeks", String(weeks));
    next.set("metric", metric);
    setSp(next, { replace: true });
  }, [a, b, weeks, metric, setSp]);

  const marketsQ = useQuery({ queryKey: ["markets"], queryFn: fetchMarkets });
  const hA = useQuery({ queryKey: ["history", a, weeks], queryFn: () => fetchHistory(a, weeks), enabled: !!a });
  const hB = useQuery({ queryKey: ["history", b, weeks], queryFn: () => fetchHistory(b, weeks), enabled: !!b });

  const data = useMemo(() => {
    const byDate = {};
    (hA.data?.rows || []).forEach((r) => {
      byDate[r.report_date] = { date: r.report_date, A: r[metric] };
    });
    (hB.data?.rows || []).forEach((r) => {
      byDate[r.report_date] = { ...(byDate[r.report_date] || { date: r.report_date }), B: r[metric] };
    });
    return Object.values(byDate).sort((x, y) => (x.date < y.date ? -1 : 1));
  }, [hA.data, hB.data, metric]);

  const meta = marketsQ.data?.markets || [];
  const labelA = hA.data ? `${hA.data.symbol} · ${hA.data.name}` : "—";
  const labelB = hB.data ? `${hB.data.symbol} · ${hB.data.name}` : "—";

  const latestA = hA.data?.rows?.at(-1);
  const latestB = hB.data?.rows?.at(-1);

  const metricLabel = {
    net_noncomm: "Net Non-Commercial",
    net_comm: "Net Commercial",
    open_interest: "Open Interest",
    noncomm_long: "NC Long",
    noncomm_short: "NC Short",
  }[metric] || metric;

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="compare-page">
      <header className="border-b border-zinc-800 bg-black/95 backdrop-blur sticky top-0 z-20">
        <div className="px-3 lg:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="back-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-700 px-2 py-1 text-[11px] uppercase tracking-widest text-zinc-300 hover:border-amber-500 hover:text-amber-term"
            >
              <ArrowLeft size={12} strokeWidth={1.5} /> Back
            </Link>
            <span className="text-sm font-semibold tracking-[0.18em] uppercase text-amber-term">
              COMPARE
            </span>
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 hidden md:inline">
              {labelA} VS {labelB}
            </span>
          </div>
        </div>
      </header>

      <main className="px-3 lg:px-6 py-3 space-y-3">
        <div
          className="border border-zinc-800 bg-[#09090B] p-2 flex items-center gap-2 flex-wrap"
          data-testid="compare-controls"
        >
          <Selector label="A" value={a} onChange={setA} options={meta} testid="select-a" colorDot="#06B6D4" />
          <Selector label="B" value={b} onChange={setB} options={meta} testid="select-b" colorDot="#F59E0B" />
          <div className="flex items-center gap-1 ml-2" data-testid="metric-tabs">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 pr-1">METRIC</span>
            {[
              ["net_noncomm", "NET NC"],
              ["net_comm", "NET C"],
              ["open_interest", "OI"],
              ["noncomm_long", "NC LONG"],
              ["noncomm_short", "NC SHORT"],
            ].map(([k, l]) => (
              <button
                key={k}
                data-testid={`metric-${k}`}
                onClick={() => setMetric(k)}
                className={`px-2 py-1 text-[11px] uppercase tracking-widest border ${
                  metric === k
                    ? "border-amber-500 bg-amber-500/10 text-amber-term"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 ml-auto" data-testid="weeks-tabs">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 pr-1">RANGE</span>
            {WEEKS_OPTS.map((w) => (
              <button
                key={w}
                data-testid={`weeks-${w}`}
                onClick={() => setWeeks(w)}
                className={`px-2 py-1 text-[11px] uppercase tracking-widest border ${
                  weeks === w
                    ? "border-amber-500 bg-amber-500/10 text-amber-term"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                {w}W
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SideStat color="#06B6D4" label={labelA} latest={latestA} metric={metric} testid="side-a" />
          <SideStat color="#F59E0B" label={labelB} latest={latestB} metric={metric} testid="side-b" />
        </div>

        <div className="border border-zinc-800 bg-[#09090B]" data-testid="compare-chart">
          <div className="px-3 py-2 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-400">
            ▍ {metricLabel} · Overlay · {weeks}W
          </div>
          <div className="p-2 h-[480px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => (d ? d.slice(5) : "")} minTickGap={28} />
                <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={fmtInt} width={80} />
                <Tooltip content={<TipBox />} cursor={{ stroke: "#F59E0B", strokeDasharray: "2 2" }} />
                <Legend wrapperStyle={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#A1A1AA", fontFamily: "JetBrains Mono" }} iconType="square" />
                <ReferenceLine y={0} stroke="#3F3F46" strokeDasharray="2 2" />
                <Line type="linear" dataKey="A" name={labelA} stroke="#06B6D4" strokeWidth={1.75} dot={false} />
                <Line type="linear" dataKey="B" name={labelB} stroke="#F59E0B" strokeWidth={1.75} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <footer className="text-[11px] uppercase tracking-widest text-zinc-600 pt-2 pb-6">
          Source: CFTC Legacy Futures Only · Share link reflects current filters.
        </footer>
      </main>
    </div>
  );
}

function Selector({ label, value, onChange, options, testid, colorDot }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
      <span className="inline-block h-2 w-2" style={{ background: colorDot }} />
      <span>{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-black border border-zinc-700 px-2 py-1 text-xs text-zinc-100 rounded-none focus:border-amber-500 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.symbol} — {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function SideStat({ color, label, latest, metric, testid }) {
  return (
    <div className="border border-zinc-800 bg-[#09090B] p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block h-2 w-2" style={{ background: color }} />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      </div>
      {latest ? (
        <div className="flex items-baseline gap-3">
          <span className={`text-xl font-semibold tabular-nums ${signClass(latest[metric])}`}>
            {fmtSigned(latest[metric])}
          </span>
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">{latest.report_date}</span>
        </div>
      ) : (
        <span className="text-zinc-600 text-xs uppercase tracking-widest">loading…</span>
      )}
    </div>
  );
}
